import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import {
	createMultiAddForm,
	deleteMultiAddForm,
	demoteMultiAddForm,
	getMultiAddFormLists,
	promoteMultiAddForm,
	updateMultiAddForm,
} from '../services/multiAddForms';
import { normalizeId } from '../utils/ids';

/**
 * The collection's multi add forms, in the useQuickAddTemplates idiom: every
 * mutation applies optimistically and rolls the whole list back if the
 * server refuses. No provider — forms are scoped to one collection and
 * fetched by the surface that shows them.
 *
 * `enabled` lets a host defer the fetch until it is actually needed (the Add
 * item modal asks for the list only once it opens).
 *
 * Mutations a user can plausibly get WRONG (a duplicate name → 409, an
 * illegal mapping → 400) reject after rolling back, so the editor can show
 * the server's own message where the user is still typing.
 */

/** The two tiers a form can live in — the same split as favorites. */
export const MULTI_ADD_SCOPE = { COLLECTION: 'collection', USER: 'user' };

const tierRank = scope => (scope === MULTI_ADD_SCOPE.COLLECTION ? 0 : 1);

/** Collection tier first, name order within each — the server's own ordering. */
export const sortMultiAddForms = list => [...list].sort((a, b) => (
	tierRank(a?.scope) - tierRank(b?.scope)
	|| (a?.name || '').localeCompare(b?.name || '')
));

const replaceById = (list, id, next) => sortMultiAddForms(
	list.map(entry => (normalizeId(entry?._id) === id ? { ...entry, ...next } : entry))
);

export default function useMultiAddForms(collectionId, { enabled = true } = {}) {
	const { message } = App.useApp();
	// The collection's own forms only: a sub-collection inherits none from
	// the collections above it (owner ruling 2026-10-21, KPR-1084).
	const [forms, setForms] = useState([]);
	const [loading, setLoading] = useState(false);
	// The committed list, for the rollback snapshot: a state updater may run
	// lazily rather than at the call, so a snapshot taken only inside one is
	// not guaranteed to exist by the time the mutation fails.
	const formsRef = useRef(forms);
	formsRef.current = forms;
	const [fetchKey, setFetchKey] = useState(0);
	// A first read has answered for this collection, success or failure: until
	// then `loading` is true, so no list draws "No multi adds yet" ahead of it.
	const [settledFor, setSettledFor] = useState(null);
	const [loadError, setLoadError] = useState(false);

	useEffect(() => {
		if (!collectionId || !enabled) return undefined;
		let active = true;
		setLoading(true);
		setLoadError(false);
		getMultiAddFormLists(collectionId)
			.then(({ forms: list }) => {
				if (!active) return;
				setForms(sortMultiAddForms(list));
			})
			// Multi adds are an enhancement over a modal that works without
			// them; a failed load leaves the Add item dialog empty and quiet,
			// and says so to the list that asks (`loadError`), which offers Retry.
			.catch(() => { if (active) { setForms([]); setLoadError(true); } })
			.finally(() => {
				if (!active) return;
				setLoading(false);
				setSettledFor(collectionId);
			});
		return () => { active = false; };
	}, [collectionId, enabled, fetchKey]);

	useEffect(() => { setForms([]); }, [collectionId]);

	const reload = useCallback(() => setFetchKey(key => key + 1), []);

	const run = useCallback(async (optimistic, mutation, { errorText, rethrow = false } = {}) => {
		let snapshot = formsRef.current;
		setForms(current => { snapshot = current; return optimistic(current); });
		try {
			const result = await mutation();
			if (result) {
				const id = normalizeId(result._id);
				setForms(current => {
					const known = current.some(entry => normalizeId(entry?._id) === id);
					return known ? replaceById(current, id, result) : sortMultiAddForms([...current, result]);
				});
			}
			return result;
		} catch (error) {
			setForms(snapshot);
			if (rethrow) throw error;
			message.error(error?.response?.data?.message || errorText);
			return null;
		}
	}, [message]);

	const create = useCallback((draft) => {
		const pending = { ...draft, _id: `pending-${Date.now()}`, scope: MULTI_ADD_SCOPE.USER, pending: true };
		return run(
			current => sortMultiAddForms([...current, pending]),
			() => createMultiAddForm(collectionId, draft)
				.then(saved => { setForms(cur => cur.filter(e => e !== pending)); return saved; })
				.catch(error => { setForms(cur => cur.filter(e => e !== pending)); throw error; }),
			{ errorText: 'Could not save the multi add.', rethrow: true }
		);
	}, [collectionId, run]);

	const update = useCallback((form, patch) => {
		const id = normalizeId(form?._id);
		return run(
			current => replaceById(current, id, patch),
			() => updateMultiAddForm(collectionId, id, patch),
			{ errorText: 'Could not save the multi add.', rethrow: true }
		);
	}, [collectionId, run]);

	const remove = useCallback((form) => {
		const id = normalizeId(form?._id);
		return run(
			current => current.filter(entry => normalizeId(entry?._id) !== id),
			() => deleteMultiAddForm(collectionId, id).then(() => null),
			{ errorText: 'Could not delete the multi add.' }
		);
	}, [collectionId, run]);

	const promote = useCallback((form) => {
		const id = normalizeId(form?._id);
		return run(
			current => replaceById(current, id, { scope: MULTI_ADD_SCOPE.COLLECTION }),
			() => promoteMultiAddForm(collectionId, id),
			{ errorText: 'Could not pin the multi add for everyone.' }
		);
	}, [collectionId, run]);

	const demote = useCallback((form) => {
		const id = normalizeId(form?._id);
		return run(
			current => replaceById(current, id, { scope: MULTI_ADD_SCOPE.USER }),
			() => demoteMultiAddForm(collectionId, id),
			{ errorText: 'Could not unpin the multi add.' }
		);
	}, [collectionId, run]);

	const waiting = loading || Boolean(collectionId && enabled && settledFor !== collectionId);

	return { forms, loading: waiting, loadError, reload, create, update, remove, promote, demote };
}
